import React, { useState, useMemo } from 'react';
import { Calendar, Download, FileText, Table, Mail, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { ExportService, ExportColumn } from '@/services/exportService';
import { format } from 'date-fns';

interface ExportConfigPanelProps {
  data: any[];
  onPreview: (config: ExportConfig) => void;
  onExport: (config: ExportConfig) => void;
  onEmailReport: (config: ExportConfig) => void;
}

export interface ExportConfig {
  dateRange: { from: Date | null; to: Date | null };
  dataTypes: string[];
  patterns: string[];
  columns: string[];
  format: 'csv' | 'json' | 'pdf' | 'excel';
  filename: string;
}

const availableColumns = [
  { key: 'id', label: 'Submission ID', required: true },
  { key: 'submitted_at', label: 'Timestamp', required: true },
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'ip_address', label: 'IP Address' },
  { key: 'zip_code', label: 'Zip Code' },
  { key: 'risk_score', label: 'Risk Score' },
  { key: 'detected_patterns', label: 'Fraud Patterns' },
  { key: 'status', label: 'Status' },
  { key: 'source', label: 'Source' },
  { key: 'user_agent', label: 'User Agent' },
  { key: 'saved_amount', label: 'Amount Saved' }
];

const dataTypeOptions = [
  { id: 'fraudulent', label: 'Fraudulent (Risk ≥ 70)', count: 1039 },
  { id: 'suspicious', label: 'Suspicious (Risk 40-69)', count: 234 },
  { id: 'legitimate', label: 'Legitimate (Risk < 40)', count: 1974 },
  { id: 'whitelisted', label: 'Whitelisted', count: 45 }
];

const patternOptions = [
  { id: 'sequential', label: 'Sequential Phones', count: 312 },
  { id: 'datacenter', label: 'Data Center IPs', count: 892 },
  { id: 'zip', label: 'Zip Concentration', count: 432 },
  { id: 'email', label: 'Suspicious Emails', count: 234 },
  { id: 'velocity', label: 'Velocity Attacks', count: 67 }
];

export function ExportConfigPanel({ data, onPreview, onExport, onEmailReport }: ExportConfigPanelProps) {
  const [config, setConfig] = useState<ExportConfig>({
    dateRange: { from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), to: new Date() },
    dataTypes: ['fraudulent'],
    patterns: ['sequential', 'datacenter', 'zip'],
    columns: ['id', 'submitted_at', 'first_name', 'last_name', 'email', 'phone', 'ip_address', 'risk_score', 'detected_patterns'],
    format: 'csv',
    filename: 'fraud-leads-export'
  });

  const filteredData = useMemo(() => {
    return data.filter(item => {
      // Apply date range filter
      if (config.dateRange.from && config.dateRange.to) {
        const itemDate = new Date(item.submitted_at);
        if (itemDate < config.dateRange.from || itemDate > config.dateRange.to) {
          return false;
        }
      }
      
      // Apply data type filter
      if (config.dataTypes.length > 0) {
        const riskScore = item.risk_score || 0;
        const matchesType = config.dataTypes.some(type => {
          switch (type) {
            case 'fraudulent': return riskScore >= 70;
            case 'suspicious': return riskScore >= 40 && riskScore < 70;
            case 'legitimate': return riskScore < 40;
            case 'whitelisted': return item.status === 'whitelisted';
            default: return false;
          }
        });
        if (!matchesType) return false;
      }
      
      return true;
    });
  }, [data, config.dateRange, config.dataTypes]);

  const estimatedFileSize = useMemo(() => {
    return ExportService.calculateFileSize(filteredData, config.format);
  }, [filteredData, config.format]);

  const handleDataTypeChange = (typeId: string, checked: boolean) => {
    setConfig(prev => ({
      ...prev,
      dataTypes: checked 
        ? [...prev.dataTypes, typeId]
        : prev.dataTypes.filter(id => id !== typeId)
    }));
  };

  const handlePatternChange = (patternId: string, checked: boolean) => {
    setConfig(prev => ({
      ...prev,
      patterns: checked 
        ? [...prev.patterns, patternId]
        : prev.patterns.filter(id => id !== patternId)
    }));
  };

  const handleColumnChange = (columnKey: string, checked: boolean) => {
    const column = availableColumns.find(col => col.key === columnKey);
    if (column?.required && !checked) return; // Don't allow unchecking required columns
    
    setConfig(prev => ({
      ...prev,
      columns: checked 
        ? [...prev.columns, columnKey]
        : prev.columns.filter(key => key !== columnKey)
    }));
  };

  return (
    <div className="space-y-6">
      {/* Export Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Lead Data
          </CardTitle>
          <div className="text-sm text-muted-foreground">
            <Badge variant="secondary" className="mr-2">
              {filteredData.length} records selected
            </Badge>
            {filteredData.filter(item => item.risk_score >= 70).length} fraudulent, {' '}
            {filteredData.filter(item => item.risk_score < 40).length} legitimate
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Date Range */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Date Range</Label>
            <DatePickerWithRange
              onDateChange={(dateRange) => setConfig(prev => ({ ...prev, dateRange }))}
            />
          </div>

          {/* Data Type Filter */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Lead Type</Label>
            <div className="grid grid-cols-2 gap-3">
              {dataTypeOptions.map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={option.id}
                    checked={config.dataTypes.includes(option.id)}
                    onCheckedChange={(checked) => handleDataTypeChange(option.id, checked as boolean)}
                  />
                  <Label htmlFor={option.id} className="text-sm">
                    {option.label}
                    <Badge variant="outline" className="ml-2 text-xs">
                      {option.count}
                    </Badge>
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Pattern Filter */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Include Patterns</Label>
            <div className="grid grid-cols-2 gap-3">
              {patternOptions.map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={option.id}
                    checked={config.patterns.includes(option.id)}
                    onCheckedChange={(checked) => handlePatternChange(option.id, checked as boolean)}
                  />
                  <Label htmlFor={option.id} className="text-sm">
                    {option.label}
                    <Badge variant="outline" className="ml-2 text-xs">
                      {option.count}
                    </Badge>
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Column Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Select Columns to Export</Label>
            <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
              {availableColumns.map((column) => (
                <div key={column.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={column.key}
                    checked={config.columns.includes(column.key)}
                    onCheckedChange={(checked) => handleColumnChange(column.key, checked as boolean)}
                    disabled={column.required}
                  />
                  <Label htmlFor={column.key} className="text-sm">
                    {column.label}
                    {column.required && (
                      <Badge variant="secondary" className="ml-1 text-xs">
                        Required
                      </Badge>
                    )}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Export Format */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Export Format</Label>
            <RadioGroup
              value={config.format}
              onValueChange={(value: 'csv' | 'json' | 'pdf' | 'excel') => 
                setConfig(prev => ({ ...prev, format: value }))
              }
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="csv" id="csv" />
                <Label htmlFor="csv" className="flex flex-col">
                  <span>CSV (Excel Compatible)</span>
                  <span className="text-xs text-muted-foreground">Best for data analysis</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="json" id="json" />
                <Label htmlFor="json" className="flex flex-col">
                  <span>JSON</span>
                  <span className="text-xs text-muted-foreground">For API integration</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pdf" id="pdf" />
                <Label htmlFor="pdf" className="flex flex-col">
                  <span>PDF Report</span>
                  <span className="text-xs text-muted-foreground">For stakeholder presentation</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="excel" id="excel" />
                <Label htmlFor="excel" className="flex flex-col">
                  <span>Excel Workbook</span>
                  <span className="text-xs text-muted-foreground">Multiple sheets with formatting</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Filename */}
          <div className="space-y-2">
            <Label htmlFor="filename" className="text-sm font-medium">Filename</Label>
            <Input
              id="filename"
              value={config.filename}
              onChange={(e) => setConfig(prev => ({ ...prev, filename: e.target.value }))}
              placeholder="Enter filename"
            />
          </div>

          {/* Export Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              Estimated file size: <span className="font-medium">{estimatedFileSize}</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onPreview(config)}>
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              <Button variant="outline" onClick={() => onEmailReport(config)}>
                <Mail className="h-4 w-4 mr-2" />
                Email Report
              </Button>
              <Button onClick={() => onExport(config)}>
                <Download className="h-4 w-4 mr-2" />
                Download Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}